import useWebSocket from "react-use-websocket";
import {
  createLinkingTrace,
  getTraceCarrierForSpan,
  traceIfParentExists,
  tracer,
} from "./observability/logging";
import { getAccessToken } from "./accessToken";
import { useCallback, useEffect, useRef } from "react";
import { Span, SpanStatusCode } from "@opentelemetry/api";
import { useAppSelector } from "./hooks/store";

export interface FlashPCBWebsocketMessage {
  type: string;
}

interface TraceCarrierMessage extends FlashPCBWebsocketMessage {
  type: "trace";
  trace_carrier: Record<string, string>;
}

export interface FlashPCBWebsocketOptions {
  /**
   * Called right after the auth handshake message has been sent on a new
   * (or reconnected) socket. Anything the server needs after auth - e.g. a
   * subscription request - should be sent from here so it is ordered after
   * the token and re-sent on every reconnect.
   */
  onAuthenticated?: () => void;
  onClose?: () => void;
}

export default function useFlashPCBWebsocket<
  M extends FlashPCBWebsocketMessage,
>(
  websocketUrl: string | null,
  onMessage: (message: M) => void,
  terminalCloseReasons?: Set<string>,
  options?: FlashPCBWebsocketOptions,
) {
  const websocketSpanRef = useRef<Span | null>(null);
  const reconnectAttemptRef = useRef<number>(0);
  const userId = useAppSelector((state) => state.auth.userId);

  useEffect(() => {
    reconnectAttemptRef.current = 0;
  }, [websocketUrl]);

  const handleEvent = useCallback(
    (event: MessageEvent, parentSpan: Span) => {
      try {
        const message = JSON.parse(event.data) as M | TraceCarrierMessage;
        parentSpan.setAttribute("messageType", message.type);
        if (message.type === "trace") {
          createLinkingTrace((message as TraceCarrierMessage).trace_carrier);
        } else {
          onMessage(message as M);
        }
      } catch (e) {
        parentSpan.recordException(e as Error);
        parentSpan.setStatus({
          code: SpanStatusCode.ERROR,
          message: e instanceof Error ? e.message : String(e),
        });
        throw e;
      }
    },
    [onMessage],
  );

  const isTerminalClose = useCallback(
    (event: CloseEvent) => {
      // Close reasons the server sends with code 1008 (policy violation) that mean
      // "don't bother coming back" — reconnecting can never succeed for these.
      if (terminalCloseReasons !== undefined) {
        return event.code === 1008 && terminalCloseReasons.has(event.reason);
      }
    },
    [terminalCloseReasons],
  );

  const { sendJsonMessage } = useWebSocket(websocketUrl, {
    onOpen: () => {
      tracer.startActiveSpan(
        "websocketConnection",
        {
          attributes: {
            userId: userId || undefined,
            reconnectAttempt: reconnectAttemptRef.current,
            websocketUrl: websocketUrl || "",
          },
        },
        async (onOpenWebsocket) => {
          websocketSpanRef.current = onOpenWebsocket;
          const cognitoAccessToken = await getAccessToken();
          const token = cognitoAccessToken
            ? `Bearer ${cognitoAccessToken.toString()}`
            : null;
          traceIfParentExists(onOpenWebsocket, "sendInitialMessage", (span) => {
            try {
              sendJsonMessage({
                token,
                trace_carrier: getTraceCarrierForSpan(span),
              });
            } finally {
              span.end();
            }
          });
          options?.onAuthenticated?.();
        },
      );
    },
    onClose: (event) => {
      const closeAttributes = {
        closeEventCode: event.code,
        closeEventReason: event.reason,
        closeEventWasClean: event.wasClean,
        closeEventIsTerminal: isTerminalClose(event),
        reconnectAttempt: reconnectAttemptRef.current,
        documentVisibilityState: document.visibilityState,
        navigatorOnLine: navigator.onLine,
      };
      if (websocketSpanRef.current !== null) {
        websocketSpanRef.current.setAttributes(closeAttributes);
        websocketSpanRef.current.end();
        websocketSpanRef.current = null;
      } else {
        // Handshake failed before onOpen fired. Record an info-level span so
        // we can still see the close event, but don't treat it as an error —
        // routine reconnect-handshake failures (HMR, brief backend hiccups,
        // sleep/wake) fire here and would otherwise drown out real signal.
        tracer.startActiveSpan(
          "websocketHandshakeFailure",
          {
            attributes: {
              userId: userId || undefined,
              websocketUrl: websocketUrl || "",
              ...closeAttributes,
            },
          },
          (span) => span.end(),
        );
      }
      reconnectAttemptRef.current += 1;
      options?.onClose?.();
    },
    //Will attempt to reconnect on all close events, such as server shutting
    //down, except ones the server marks as terminal (e.g. board not found)
    shouldReconnect: (event) => !isTerminalClose(event),
    // Exponential backoff with "equal jitter": wait at least half the backoff
    // and a random amount of the other half. Without jitter, every socket on
    // a page (one per part/board) that dropped at the same moment - deploy,
    // laptop wake, network blip - reconnects in lockstep and hits the server
    // as one burst.
    reconnectInterval: (attemptNumber) => {
      const backoffMs = Math.min(Math.pow(2, attemptNumber) * 1000, 10000);
      return backoffMs / 2 + Math.random() * (backoffMs / 2);
    },
    onMessage: (event) => {
      traceIfParentExists(
        websocketSpanRef.current,
        "receivedWebSocketMessage",
        (span) => {
          try {
            handleEvent(event, span);
          } finally {
            span.end();
          }
        },
      );
    },
    reconnectAttempts: Infinity,
  });

  return { sendJsonMessage };
}
